/* The complete inventory of where one user's data lives. Deleting an account
 * has to erase ALL of it, and the Firestore client SDK cannot enumerate a
 * document's subcollections — only the server can — so the list has to be
 * written down by hand. That makes it the one thing in this codebase that
 * goes stale silently: add `users/{uid}/notes` somewhere and account deletion
 * would quietly leave it behind forever, since the auth user is gone and
 * nobody can ever reach it again.
 *
 * `userDataPaths.test.js` therefore greps every Firestore path in src/ and
 * fails if it references a subcollection that isn't listed here. Keep this
 * file free of imports (especially firebase) so that test stays cheap.
 */

/* Subcollections of users/{uid}. `nested` names a subcollection hanging off
 * each of that collection's documents (only ledgerCaches has one), which is
 * as deep as the tree goes — a third level would need this to become a real
 * tree, and the test below would catch that. */
export const USER_SUBCOLLECTIONS = [
  { id: "app", note: "ledger prefs, last-open tab, notification preferences" },
  { id: "data", note: "the Learn state blob (XP, streak, badges, Leitner cards, settings)" },
  { id: "ledgerCaches", nested: "chunks", note: "gzipped game history, one meta doc + N chunk docs" },
];

/* Top-level collections keyed by uid, outside users/{uid}.
 *
 * `entitlements/{uid}` is deliberately absent: rules make it client-unwritable
 * (only the console or a payment webhook may touch it), and it holds a single
 * `pro` boolean with no personal data. Once the auth user is deleted its uid is
 * never reissued, so what's left is an unreachable flag rather than a record of
 * a person. When real billing ships, deletion should move server-side and take
 * this doc — plus the subscription itself — with it. */
export const UID_KEYED_COLLECTIONS = [
  { id: "proInterest", note: "painted-door Pro signup: uid, email, chosen plan" },
  { id: "acquisition", note: "first-touch signup channel: source, referrer host, timestamp" },
];

/* Top-level collections holding this user's data under document ids that are
 * NOT the bare uid, so deletion has to find them by field instead.
 *
 * The outgoing mail queue is keyed `{uid}_{weekKey}` because a user can have
 * several messages and Firestore ids must be unique — and the id shape is
 * itself load-bearing, since firestore.rules uses it to make "create" mean
 * "at most one message per week". Contact messages are addDoc-random for the
 * same reason minus the rate limit: there can be many per user, and unlike
 * the recap nothing needs to find one by name.
 *
 * These carry an email address, which is why they must go at all. The query is
 * `where(field, "==", uid)`; rules permit it because the same predicate is
 * what authorizes reading each document. */
export const UID_QUERIED_COLLECTIONS = [
  { id: "mail", field: "uid", note: "queued weekly-recap emails: recipient address + the rendered message" },
  { id: "feedback", field: "uid", note: "messages sent through Contact: reply address + the message text" },
];

/* Collections that are NOT this user's data, listed so the inventory test can
 * tell "deliberately excluded" from "forgotten". `config` is a single public
 * document naming which optional channels this installation has; `entitlements`
 * and `catalog` are covered in the notes above. `landing_visits` holds one
 * record per landing-page view with no uid and no identifier at all
 * (landingVisit.js), so there is nothing in it an account could be found by,
 * and it expires by itself after 90 days. */
export const NON_USER_COLLECTIONS = ["catalog", "config", "entitlements", "landing_visits"];

/* Everything the app keeps in localStorage. Wiping these matters as much as
 * the cloud copy: leaving `towardsgm:learn` behind would show the next visitor
 * on this device the deleted user's progress, and leaving `towardsgm:pro`
 * behind would leave Pro content unlocked. */
export const LOCAL_STORAGE_KEYS = [
  "towardsgm:analytics-consent",
  "towardsgm:billing",
  "towardsgm:catalog",
  "towardsgm:catalog-pro",
  "towardsgm:learn",
  "towardsgm:notify",
  "towardsgm:notify-due",
  "towardsgm:owner",
  "towardsgm:prefs",
  "towardsgm:pro",
  "towardsgm:pro-web-hint",
  "towardsgm:tab",
];

/* The subset of those that describe the DEVICE rather than whoever is signed
 * in, so signing out leaves them alone (deviceData.js#wipeAccountData):
 * consent is this browser's answer to this browser's prompt, and the catalog
 * cache is the public catalog. Every other key is account data, and the next
 * account to sign in here must not inherit it — which is why this is an
 * allowlist: a key nobody classified is wiped, not leaked. */
export const DEVICE_LOCAL_STORAGE_KEYS = ["towardsgm:analytics-consent", "towardsgm:catalog"];
